import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import SyncIcon from '@mui/icons-material/Sync';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import apiService from '../services/api.service';
import PRWorkflowFormPreview from './components/PRWorkflowFormPreview';
import type { PRPreviewStep } from './components/PRWorkflowFormPreview';

// ─── Types ───────────────────────────────────────────────────────────────────

interface AnchorOption {
    key: string;
    label: string;
    tabType: 'signature' | 'initial';
}

interface AssignedUser {
    userid: number;
    fullname: string;
    email: string;
    ordering?: number;
    isactive?: boolean;
    docusign_user_api_id?: string | null;
}

interface SigningGroupCache {
    docusign_signing_group_id: string;
    docusign_signing_group_name: string;
}

interface WorkflowStep {
    workflowid?: number;
    stepnumber: number;
    stagename: string;
    approverrole: string;
    approvertype: 'Single' | 'Multi';
    ismandatory: boolean;
    minamount: string; // string in form state, parsed to number on save
    maxamount: string;
    thresholdcurrency: string;
    tab_type: 'signature' | 'initial' | null;
    anchor_key: string | null;
    x_offset: number;
    y_offset: number;
    enabled: boolean;
    /**
     * When false, this step stays in the workflow (still gates via threshold,
     * still appears in the editor and audit trail) but is omitted from the
     * DocuSign envelope. Useful for steps like AP Team whose in-app submit
     * gate already exists outside DocuSign — saves a recipient slot on
     * limited-recipient DocuSign accounts.
     */
    include_in_docusign: boolean;
    status_on_complete: string | null;
    users: AssignedUser[];
    signinggroup: SigningGroupCache | null;
    /** Local UI flag — has this row been touched since load? */
    dirty?: boolean;
}

interface CompanyOption {
    companyid: number;
    companyname: string;
    companycode?: string;
    /**
     * Effective DocuSign flag for Payment Requests on this company. When false,
     * the workflow editor skips the per-user DocuSign-API-ID validation and
     * adjusts the post-save success message — there's no DocuSign tenant to
     * sync against. Source: companies.paymentrequestdocusignenabled (override)
     * or companies.docusignenabled (default), with FALSE as the final fallback.
     */
    docusignenabled?: boolean | null;
    paymentrequestdocusignenabled?: boolean | null;
}

/**
 * Resolve the effective DocuSign flag for a company in the workflow editor.
 * Mirrors the COALESCE used server-side
 * (docuSignPaymentRequestService.isDocuSignEnabled).
 */
const resolveCompanyDocuSignEnabled = (c: CompanyOption | undefined | null): boolean => {
    if (!c) return false;
    if (typeof c.paymentrequestdocusignenabled === 'boolean') {
        return c.paymentrequestdocusignenabled;
    }
    return c.docusignenabled === true;
};

const STATUS_ON_COMPLETE_VALUES = [
    '',
    'APPrepared',
    'ProcurementDirectorApproved',
    'CAReviewed',
    'FMApproved',
    'PCDDirectorApproved',
    'GMApproved',
    'FinalApproved',
];

// ─── Helpers ────────────────────────────────────────────────────────────────

const apiToStep = (s: any): WorkflowStep => ({
    workflowid: s.workflowid,
    stepnumber: Number(s.stepnumber ?? 0),
    stagename: String(s.stagename ?? ''),
    approverrole: String(s.approverrole ?? ''),
    approvertype: s.approvertype === 'Multi' ? 'Multi' : 'Single',
    ismandatory: Boolean(s.ismandatory ?? true),
    minamount: s.minamount == null ? '' : String(s.minamount),
    maxamount: s.maxamount == null ? '' : String(s.maxamount),
    thresholdcurrency: String(s.thresholdcurrency ?? ''),
    tab_type:
        s.tab_type === 'signature' || s.tab_type === 'initial' ? s.tab_type : null,
    anchor_key: s.anchor_key ?? null,
    x_offset: Number(s.x_offset ?? 0),
    y_offset: Number(s.y_offset ?? 0),
    enabled: Boolean(s.enabled ?? true),
    include_in_docusign: s.include_in_docusign === false ? false : true,
    status_on_complete: s.status_on_complete ?? null,
    users: Array.isArray(s.users) ? s.users : [],
    signinggroup: s.signinggroup ?? null,
});

const stepToPayload = (s: WorkflowStep) => ({
    workflowid: s.workflowid,
    stepnumber: s.stepnumber,
    stagename: s.stagename || null,
    approverrole: s.approverrole,
    approvertype: s.approvertype,
    ismandatory: s.ismandatory,
    // Threshold semantics: a step without a currency is unconditional —
    // min/max are meaningless without a currency to compare against. So
    // when thresholdcurrency is empty, NULL out min/max too. This keeps
    // editor saves clean (no stray "0" values without a currency that
    // would later silently skip the step in resolveApproverChain).
    minamount:
        !s.thresholdcurrency || s.minamount === ''
            ? null
            : Number(s.minamount),
    maxamount:
        !s.thresholdcurrency || s.maxamount === ''
            ? null
            : Number(s.maxamount),
    thresholdcurrency: s.thresholdcurrency || null,
    tab_type: s.tab_type,
    anchor_key: s.anchor_key,
    x_offset: s.x_offset,
    y_offset: s.y_offset,
    enabled: s.enabled,
    include_in_docusign: s.include_in_docusign,
    status_on_complete: s.status_on_complete || null,
    users: s.users.map((u, idx) => ({
        userid: u.userid,
        ordering: u.ordering ?? idx,
        isactive: u.isactive ?? true,
    })),
});

const stepToPreview = (s: WorkflowStep): PRPreviewStep => ({
    stepnumber: s.stepnumber,
    approverrole: s.approverrole,
    tab_type: s.tab_type,
    anchor_key: s.anchor_key,
    x_offset: s.x_offset,
    y_offset: s.y_offset,
    // Preview shows only steps that actually appear on the DocuSign envelope.
    enabled: s.enabled && s.include_in_docusign,
});

// ─── Component ───────────────────────────────────────────────────────────────

export default function PaymentRequestWorkflow() {
    const [companies, setCompanies] = useState<CompanyOption[]>([]);
    const [companyId, setCompanyId] = useState<number | null>(null);

    const [steps, setSteps] = useState<WorkflowStep[]>([]);
    const [anchors, setAnchors] = useState<AnchorOption[]>([]);

    /** Cache of eligible-users results keyed by role for the current company. */
    const [eligibleUsers, setEligibleUsers] = useState<Record<string, AssignedUser[]>>(
        {},
    );

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);
    const [expandedStep, setExpandedStep] = useState<number | false>(false);

    const dirty = useMemo(() => steps.some((s) => s.dirty), [steps]);

    // ── Load companies ─────────────────────────────────────────────────────

    useEffect(() => {
        (async () => {
            try {
                const r = await apiService.getCompanies();
                const list: CompanyOption[] = Array.isArray(r?.data) ? r.data : [];
                const active = list.filter(
                    (c: any) => c.isactive ?? c.isActive ?? true,
                );
                setCompanies(active);
                if (active.length > 0) {
                    setCompanyId(active[0].companyid);
                }
            } catch (err: any) {
                setError(err?.message || 'Failed to load companies');
            }
        })();
    }, []);

    // ── Load anchors (once) ────────────────────────────────────────────────

    useEffect(() => {
        (async () => {
            try {
                const r = await apiService.getPaymentRequestWorkflowAnchors();
                setAnchors(Array.isArray(r?.data) ? r.data : []);
            } catch (err: any) {
                // Anchors aren't critical for editing — log but don't block.
                // eslint-disable-next-line no-console
                console.warn('Failed to load anchors', err);
            }
        })();
    }, []);

    // ── Load workflow when company changes ─────────────────────────────────

    const loadWorkflow = useCallback(async () => {
        if (!companyId) return;
        setLoading(true);
        setError(null);
        setInfo(null);
        try {
            const r = await apiService.getPaymentRequestWorkflow(companyId);
            const list: WorkflowStep[] = (Array.isArray(r?.data) ? r.data : []).map(
                apiToStep,
            );
            list.sort((a, b) => a.stepnumber - b.stepnumber);
            setSteps(list);
            // Reset eligible-users cache; it's per-company.
            setEligibleUsers({});
        } catch (err: any) {
            setError(err?.message || 'Failed to load workflow');
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        loadWorkflow();
    }, [loadWorkflow]);

    // ── Eligible-users fetcher (cached per role) ───────────────────────────

    const ensureEligibleUsers = useCallback(
        async (role: string) => {
            if (!companyId || !role) return;
            if (eligibleUsers[role]) return;
            try {
                const r = await apiService.getPaymentRequestWorkflowEligibleUsers(
                    companyId,
                    role,
                );
                setEligibleUsers((prev) => ({
                    ...prev,
                    [role]: Array.isArray(r?.data) ? r.data : [],
                }));
            } catch {
                setEligibleUsers((prev) => ({ ...prev, [role]: [] }));
            }
        },
        [companyId, eligibleUsers],
    );

    // ── Mutators ───────────────────────────────────────────────────────────

    const updateStep = useCallback(
        (stepnumber: number, patch: Partial<WorkflowStep>) => {
            setSteps((prev) =>
                prev.map((s) =>
                    s.stepnumber === stepnumber
                        ? { ...s, ...patch, dirty: true }
                        : s,
                ),
            );
        },
        [],
    );

    // ── Save ───────────────────────────────────────────────────────────────

    const selectedCompany = useMemo(
        () => companies.find((c) => c.companyid === companyId) ?? null,
        [companies, companyId],
    );
    const selectedCompanyDocuSignEnabled = useMemo(
        () => resolveCompanyDocuSignEnabled(selectedCompany),
        [selectedCompany],
    );

    const onSave = useCallback(async () => {
        if (!companyId) return;
        setSaving(true);
        setError(null);
        setInfo(null);

        // Validation: every enabled non-Initiator step must have at least one
        // active assigned user. Users only need a docusign_user_api_id when
        // the company is on DocuSign for PRs — otherwise the DocuSign envelope
        // chain never runs and the mapping is unused. We always check that a
        // step has at least one active user assigned, because the in-app
        // chain still needs a recipient.
        const issues: string[] = [];
        for (const s of steps) {
            if (!s.enabled) continue;
            if (s.approverrole === 'Initiator') continue;
            // Steps marked in-app-only don't appear in the DocuSign envelope,
            // so they don't need users with DocuSign mapping. Skip the check.
            if (!s.include_in_docusign) continue;
            const active = s.users.filter((u) => u.isactive !== false);
            if (active.length === 0) {
                issues.push(
                    `Step ${s.stepnumber} (${s.approverrole}): no active users assigned.`,
                );
                continue;
            }
            // Only require DocuSign user-IDs on companies that actually use
            // DocuSign for PRs. Without this branch, every non-DocuSign
            // company couldn't save its workflow.
            if (!selectedCompanyDocuSignEnabled) continue;
            const missingDocuSign = active.filter((u) => !u.docusign_user_api_id);
            if (missingDocuSign.length > 0) {
                const names = missingDocuSign
                    .map((u) => u.fullname || u.email)
                    .join(', ');
                issues.push(
                    `Step ${s.stepnumber} (${s.approverrole}): ${names} ${missingDocuSign.length === 1 ? 'has' : 'have'} no DocuSign user mapping. Add the user(s) in DocuSign Admin → Users and paste their API Username UUID into User Management → DocuSign User API ID.`,
                );
            }
        }
        if (issues.length > 0) {
            setError(issues.join('\n'));
            setSaving(false);
            return;
        }

        try {
            const r = await apiService.updatePaymentRequestWorkflow(
                companyId,
                steps.map(stepToPayload),
            );
            // Server returns { docuSignEnabled, signingGroupsSynced } so the
            // success message reflects what actually happened. Fall back to
            // the locally-resolved company flag for older server builds.
            const serverData = (r?.data as
                | {
                      updated?: number;
                      docuSignEnabled?: boolean;
                      signingGroupsSynced?: number;
                  }
                | undefined) ?? {};
            const docuSignActive =
                typeof serverData.docuSignEnabled === 'boolean'
                    ? serverData.docuSignEnabled
                    : selectedCompanyDocuSignEnabled;
            if (docuSignActive) {
                const synced =
                    typeof serverData.signingGroupsSynced === 'number'
                        ? serverData.signingGroupsSynced
                        : steps.filter(
                              (s) =>
                                  s.enabled &&
                                  s.include_in_docusign &&
                                  s.approverrole !== 'Initiator' &&
                                  s.approvertype === 'Multi',
                          ).length;
                setInfo(
                    `Workflow saved. DocuSign signing groups synced for ${synced} step(s).`,
                );
            } else {
                setInfo('Workflow saved (in-app approval — DocuSign not enabled for this company).');
            }
            await loadWorkflow();
        } catch (err: any) {
            const msg = err?.response?.data?.message || err?.message || 'Save failed';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [companyId, steps, loadWorkflow, selectedCompanyDocuSignEnabled]);

    const onResyncSigningGroups = useCallback(async () => {
        if (!companyId) return;
        setSaving(true);
        setError(null);
        setInfo(null);
        try {
            const r = await apiService.resyncPaymentRequestWorkflowSigningGroups(
                companyId,
            );
            const results: Array<{
                stepnumber: number;
                approverrole: string;
                ok: boolean;
                message?: string;
            }> = r?.data?.results ?? [];
            const failed = results.filter((x) => !x.ok);
            if (failed.length === 0) {
                setInfo(`Re-synced ${results.length} signing group(s) with DocuSign.`);
            } else {
                setError(
                    failed
                        .map((f) => `Step ${f.stepnumber} (${f.approverrole}): ${f.message}`)
                        .join('\n'),
                );
            }
            await loadWorkflow();
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Resync failed');
        } finally {
            setSaving(false);
        }
    }, [companyId, loadWorkflow]);

    // ── Render ─────────────────────────────────────────────────────────────

    return (
        <Box component="section" aria-labelledby="prw-heading" sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
                spacing={{ xs: 1.5, md: 0 }}
                sx={{ mb: 2 }}
            >
                <Box>
                    <Typography
                        component="h1"
                        id="prw-heading"
                        variant="h5"
                        fontWeight={600}
                        sx={{ fontSize: { xs: 18, sm: 22, md: 24 } }}
                    >
                        Payment Request Workflow
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Per-company DocuSign signing chain. Edits to user
                        assignments auto-sync to DocuSign signing groups.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                        startIcon={<RefreshIcon />}
                        onClick={loadWorkflow}
                        disabled={loading || saving || !companyId}
                    >
                        Reload
                    </Button>
                    {selectedCompanyDocuSignEnabled && (
                        <Button
                            startIcon={<SyncIcon />}
                            onClick={onResyncSigningGroups}
                            disabled={loading || saving || !companyId}
                            variant="outlined"
                        >
                            Resync DocuSign Groups
                        </Button>
                    )}
                    <Button
                        startIcon={<SaveIcon />}
                        variant="contained"
                        color="primary"
                        onClick={onSave}
                        disabled={loading || saving || !dirty || !companyId}
                    >
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </Stack>
            </Stack>

            <Paper sx={{ p: 2, mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 280 }}>
                    <InputLabel id="company-select-label">Company</InputLabel>
                    <Select
                        labelId="company-select-label"
                        label="Company"
                        value={companyId ?? ''}
                        onChange={(e) => setCompanyId(Number(e.target.value))}
                    >
                        {companies.map((c) => (
                            <MenuItem key={c.companyid} value={c.companyid}>
                                {c.companycode ? `${c.companycode} — ` : ''}
                                {c.companyname}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {info && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
                    {info}
                </Alert>
            )}

            <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : steps.length === 0 ? (
                        <Alert severity="info">
                            No workflow rows for this company yet. The default
                            8-step Payment Request workflow is seeded by
                            migration; if it's missing, ask an engineer to run
                            the seed migration for this company.
                        </Alert>
                    ) : (
                        steps.map((s) => (
                            <StepRow
                                key={s.workflowid ?? `tmp-${s.stepnumber}`}
                                step={s}
                                anchors={anchors}
                                eligibleUsers={eligibleUsers[s.approverrole] || []}
                                onEnsureEligibleUsers={() =>
                                    ensureEligibleUsers(s.approverrole)
                                }
                                onChange={(patch) => updateStep(s.stepnumber, patch)}
                                expanded={expandedStep === s.stepnumber}
                                onToggleExpand={() =>
                                    setExpandedStep(
                                        expandedStep === s.stepnumber
                                            ? false
                                            : s.stepnumber,
                                    )
                                }
                            />
                        ))
                    )}
                </Grid>
                <Grid item xs={12} md={4}>
                    <PRWorkflowFormPreview
                        steps={steps.map(stepToPreview)}
                        highlightedStep={
                            expandedStep === false ? undefined : expandedStep
                        }
                    />
                </Grid>
            </Grid>
        </Box>
    );
}

// ─── StepRow ────────────────────────────────────────────────────────────────

interface StepRowProps {
    step: WorkflowStep;
    anchors: AnchorOption[];
    eligibleUsers: AssignedUser[];
    onEnsureEligibleUsers: () => void;
    onChange: (patch: Partial<WorkflowStep>) => void;
    expanded: boolean;
    onToggleExpand: () => void;
}

function StepRow(props: StepRowProps) {
    const {
        step, anchors, eligibleUsers, onEnsureEligibleUsers, onChange,
        expanded, onToggleExpand,
    } = props;

    const isInitiator = step.approverrole === 'Initiator';
    const inAppOnly = !step.include_in_docusign;
    const userIssues = useMemo(() => {
        if (isInitiator) return [];
        if (!step.enabled) return [];
        // In-app-only steps don't get a DocuSign envelope tab, so missing
        // user assignments / DocuSign mappings aren't issues for them.
        if (inAppOnly) return [];
        const issues: string[] = [];
        const active = step.users.filter((u) => u.isactive !== false);
        if (active.length === 0) issues.push('no users assigned');
        const missingDS = active.filter((u) => !u.docusign_user_api_id).length;
        if (missingDS > 0)
            issues.push(`${missingDS} user(s) missing DocuSign mapping`);
        return issues;
    }, [step, isInitiator, inAppOnly]);

    return (
        <Accordion
            expanded={expanded}
            onChange={onToggleExpand}
            TransitionProps={{ unmountOnExit: true }}
            sx={{
                mb: 1,
                opacity: step.enabled ? 1 : 0.6,
                borderLeft: step.dirty
                    ? '3px solid #f57c00'
                    : '3px solid transparent',
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                onClick={() => {
                    if (!expanded) onEnsureEligibleUsers();
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    sx={{ width: '100%' }}
                >
                    <Box
                        sx={{
                            minWidth: 36,
                            textAlign: 'center',
                            fontWeight: 700,
                            color: 'text.secondary',
                        }}
                    >
                        #{step.stepnumber}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                            {step.approverrole}
                            {isInitiator && (
                                <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ ml: 1 }}
                                >
                                    (intrinsic — uses PR creator)
                                </Typography>
                            )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {step.stagename}
                        </Typography>
                    </Box>
                    <Chip
                        size="small"
                        label={step.tab_type ?? 'no tab'}
                        color={step.tab_type === 'signature' ? 'primary' : 'default'}
                        variant={step.tab_type ? 'filled' : 'outlined'}
                    />
                    {inAppOnly && step.enabled && (
                        <Tooltip title="This step stays in the workflow but is omitted from the DocuSign envelope (in-app gate only).">
                            <Chip
                                size="small"
                                label="in-app only"
                                color="info"
                                variant="outlined"
                            />
                        </Tooltip>
                    )}
                    {!isInitiator && !inAppOnly && (
                        <Chip
                            size="small"
                            label={`${step.users.filter((u) => u.isactive !== false).length} user${step.users.length === 1 ? '' : 's'}`}
                            color={userIssues.length > 0 ? 'warning' : 'default'}
                            variant="outlined"
                        />
                    )}
                    {userIssues.length > 0 && (
                        <Tooltip title={userIssues.join(' · ')}>
                            <WarningAmberIcon color="warning" fontSize="small" />
                        </Tooltip>
                    )}
                    <FormControlLabel
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                        control={
                            <Switch
                                size="small"
                                checked={step.enabled}
                                onChange={(_, v) => onChange({ enabled: v })}
                            />
                        }
                        label={step.enabled ? 'Enabled' : 'Disabled'}
                    />
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={2}>
                    {/* Stage name */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Stage name"
                            size="small"
                            fullWidth
                            value={step.stagename}
                            onChange={(e) => onChange({ stagename: e.target.value })}
                        />
                    </Grid>
                    {/* Approver type */}
                    <Grid item xs={12} sm={6}>
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            color="primary"
                            value={step.approvertype}
                            onChange={(_, v) =>
                                v && onChange({ approvertype: v as 'Single' | 'Multi' })
                            }
                        >
                            <ToggleButton value="Single">Single</ToggleButton>
                            <ToggleButton value="Multi">Multi (OR)</ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>

                    {/* Include in DocuSign — orthogonal to Enabled. Hidden for
                        Initiator (intrinsically not a signing-group step). */}
                    {!isInitiator && (
                        <Grid item xs={12}>
                            <Tooltip
                                placement="right"
                                title="When OFF, this step still exists in the workflow (visible here, threshold rules apply, audit history captures it) but is OMITTED from the DocuSign envelope. Useful when the step's gate already happens in the app — like AP Team's submit button — and you don't want an extra DocuSign tab on the printed form. Saves a recipient slot on accounts with envelope recipient limits."
                            >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={step.include_in_docusign}
                                            onChange={(_, v) =>
                                                onChange({ include_in_docusign: v })
                                            }
                                        />
                                    }
                                    label={
                                        step.include_in_docusign
                                            ? 'Included in DocuSign envelope'
                                            : 'In-app only — NOT included in DocuSign envelope'
                                    }
                                />
                            </Tooltip>
                        </Grid>
                    )}

                    {/* Tab type */}
                    <Grid item xs={12} sm={4}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Tab type</InputLabel>
                            <Select
                                label="Tab type"
                                value={step.tab_type ?? ''}
                                onChange={(e) =>
                                    onChange({
                                        tab_type:
                                            (e.target.value as
                                                | 'signature'
                                                | 'initial'
                                                | '') || null,
                                    })
                                }
                            >
                                <MenuItem value="signature">Signature</MenuItem>
                                <MenuItem value="initial">Initial</MenuItem>
                                <MenuItem value="">— none —</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Anchor */}
                    <Grid item xs={12} sm={8}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Anchor</InputLabel>
                            <Select
                                label="Anchor"
                                value={step.anchor_key ?? ''}
                                onChange={(e) =>
                                    onChange({ anchor_key: e.target.value || null })
                                }
                            >
                                <MenuItem value="">— none —</MenuItem>
                                {anchors.map((a) => (
                                    <MenuItem key={a.key} value={a.key}>
                                        {a.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* X / Y offsets */}
                    <Grid item xs={6} sm={3}>
                        <TextField
                            label="X offset (px)"
                            size="small"
                            type="number"
                            fullWidth
                            value={step.x_offset}
                            onChange={(e) =>
                                onChange({ x_offset: Number(e.target.value) || 0 })
                            }
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            label="Y offset (px)"
                            size="small"
                            type="number"
                            fullWidth
                            value={step.y_offset}
                            onChange={(e) =>
                                onChange({ y_offset: Number(e.target.value) || 0 })
                            }
                        />
                    </Grid>

                    {/* Status on complete */}
                    <Grid item xs={12} sm={6}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Status when this step signs</InputLabel>
                            <Select
                                label="Status when this step signs"
                                value={step.status_on_complete ?? ''}
                                onChange={(e) =>
                                    onChange({
                                        status_on_complete: e.target.value || null,
                                    })
                                }
                            >
                                {STATUS_ON_COMPLETE_VALUES.map((v) => (
                                    <MenuItem key={v} value={v}>
                                        {v || '— no status change —'}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Threshold */}
                    <Grid item xs={12}>
                        <Divider textAlign="left">Threshold (optional)</Divider>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Min amount"
                            size="small"
                            type="number"
                            fullWidth
                            value={step.minamount}
                            onChange={(e) => onChange({ minamount: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Max amount"
                            size="small"
                            type="number"
                            fullWidth
                            value={step.maxamount}
                            onChange={(e) => onChange({ maxamount: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Currency"
                            size="small"
                            fullWidth
                            value={step.thresholdcurrency}
                            onChange={(e) =>
                                onChange({ thresholdcurrency: e.target.value })
                            }
                        />
                    </Grid>

                    {/* Assigned users (hidden for Initiator — uses PR creator). */}
                    {!isInitiator && (
                        <>
                            <Grid item xs={12}>
                                <Divider textAlign="left">
                                    {step.approvertype === 'Single'
                                        ? 'Assigned user — direct DocuSign recipient'
                                        : 'Assigned users — any one of them can sign (DocuSign Signing Group)'}
                                </Divider>
                            </Grid>
                            {step.approvertype === 'Single' &&
                                step.users.length > 1 && (
                                    <Grid item xs={12}>
                                        <Alert severity="warning">
                                            Single-mode step has {step.users.length} users
                                            assigned. Only the first will receive the envelope.
                                            Switch to Multi (OR) if any of them should be able
                                            to sign, or remove the extras.
                                        </Alert>
                                    </Grid>
                                )}
                            <Grid item xs={12}>
                                <Autocomplete
                                    multiple
                                    options={eligibleUsers}
                                    value={step.users}
                                    getOptionLabel={(u) =>
                                        u.fullname
                                            ? `${u.fullname} <${u.email}>`
                                            : u.email
                                    }
                                    isOptionEqualToValue={(a, b) =>
                                        a.userid === b.userid
                                    }
                                    onChange={(_, value) =>
                                        onChange({ users: value as AssignedUser[] })
                                    }
                                    renderTags={(value, getTagProps) =>
                                        value.map((u, idx) => {
                                            const tagProps = getTagProps({ index: idx });
                                            const { key, ...rest } = tagProps as any;
                                            const isPickedForSingle =
                                                step.approvertype === 'Single' && idx === 0;
                                            return (
                                                <Chip
                                                    key={key ?? u.userid}
                                                    {...rest}
                                                    size="small"
                                                    label={
                                                        u.docusign_user_api_id
                                                            ? `${u.fullname || u.email}${
                                                                  isPickedForSingle
                                                                      ? ' (will receive envelope)'
                                                                      : ''
                                                              }`
                                                            : `${u.fullname || u.email} (no DocuSign mapping)`
                                                    }
                                                    color={
                                                        u.docusign_user_api_id
                                                            ? isPickedForSingle
                                                                ? 'primary'
                                                                : 'default'
                                                            : 'warning'
                                                    }
                                                />
                                            );
                                        })
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            placeholder={
                                                eligibleUsers.length === 0
                                                    ? 'No users with this role for this company'
                                                    : step.approvertype === 'Single'
                                                      ? 'Pick one user…'
                                                      : 'Add users…'
                                            }
                                            helperText={
                                                step.users.length > 0 &&
                                                step.users.some(
                                                    (u) => !u.docusign_user_api_id,
                                                )
                                                    ? 'Users marked "no DocuSign mapping" will block envelope creation. Add them as DocuSign users first.'
                                                    : step.approvertype === 'Single'
                                                      ? `Single mode — exactly one user signs as a direct DocuSign recipient (no signing group). ${eligibleUsers.length} eligible for ${step.approverrole}`
                                                      : `Multi (OR) mode — all listed users join a DocuSign signing group; first to sign signs for all. ${eligibleUsers.length} eligible for ${step.approverrole}`
                                            }
                                        />
                                    )}
                                />
                            </Grid>
                            {step.signinggroup && step.approvertype === 'Multi' && (
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">
                                        DocuSign signing group:{' '}
                                        <code>
                                            {step.signinggroup.docusign_signing_group_name}
                                        </code>{' '}
                                        (id {step.signinggroup.docusign_signing_group_id})
                                    </Typography>
                                </Grid>
                            )}
                            {step.signinggroup && step.approvertype === 'Single' && (
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="warning.main">
                                        Note: a stale DocuSign signing group from a previous
                                        Multi-mode setup is cached for this step. It will be
                                        deleted on next Save.
                                    </Typography>
                                </Grid>
                            )}
                        </>
                    )}
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}
